import { invalidateRenewalConsumers, recordRenewalRevision, renewalQueryKind, knownRenewalRevision } from '../queries/renewalCacheUtils';
import { broadcastQueryClient } from './filteredQueryBroadcast';
import { invalidateArrScheduleConsumers } from '../queries/arrScheduleCacheUtils';

const SYNC_REGISTRY_KEY = '__qflowTanstackQueryBroadcastRegistry';

const isBrowserRuntime = () => (
  typeof window !== 'undefined'
  && typeof document !== 'undefined'
);

const isCrossTabSyncDebugEnabled = () => {
  if (!isBrowserRuntime()) return false;
  try {
    return window.localStorage?.getItem('qflowDebugCrossTabSync') === '1';
  } catch (_error) {
    return false;
  }
};

const logCrossTabSyncDebug = (message, payload = null) => {
  if (!isCrossTabSyncDebugEnabled()) return;
  if (payload === null) {
    console.log(`[QueryCrossTabSync] ${message}`);
    return;
  }
  console.log(`[QueryCrossTabSync] ${message}`, payload);
};

const sanitizeChannelSegment = (value) => (
  String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9_-]+/g, '-')
);

export const getDefaultQueryCrossTabChannel = (options = {}) => {
  const stage = sanitizeChannelSegment(options?.stage || process.env.REACT_APP_STAGE || 'local');
  const host = sanitizeChannelSegment(
    options?.host
      || (isBrowserRuntime() ? window.location.host : 'unknown')
  );
  return `qflow-rq-v5-${stage}-${host}`;
};

// Successful query data is already broadcast by TanStack, but invalidations are
// not. Send the account-level edit even when the sender has no forecast cached.
export const broadcastRenewalMatchChange = (accountId, channelName = getDefaultQueryCrossTabChannel(), matchingRevision) => {
  if (!accountId || !isBrowserRuntime() || typeof window.BroadcastChannel === 'undefined') return;
  let channel;
  try {
    channel = new window.BroadcastChannel(`${channelName}-renewal-matches`);
    channel.postMessage({ type: 'renewal-matches-changed', accountId, matchingRevision });
  } catch (error) {
    // A browser restriction must not turn a successful saved decision into an
    // apparent failed write. Same-tab invalidation has already happened.
    logCrossTabSyncDebug('Renewal change could not be sent to other tabs.', error);
  } finally {
    channel?.close();
  }
};

export const subscribeRenewalMatchChanges = (queryClient, channelName) => {
  const channel = new window.BroadcastChannel(`${channelName}-renewal-matches`);
  channel.onmessage = async ({ data }) => {
    if (data?.type !== 'renewal-matches-changed' || typeof data.accountId !== 'string' || !data.accountId) return;
    if (Number.isFinite(data.matchingRevision) && data.matchingRevision <= knownRenewalRevision(queryClient, data.accountId)) return;
    // Inactive queries stay invalidated for the next mount/reload. Active ones
    // retain data while fetching with the receiving tab's own credentials.
    await queryClient.cancelQueries({predicate: query => !!renewalQueryKind(query.queryKey, data.accountId)});
    const versioned = recordRenewalRevision(queryClient, data.accountId, data.matchingRevision);
    invalidateRenewalConsumers(queryClient, data.accountId, {refetchForecasts: !versioned});
  };
  return () => channel.close();
};

export const broadcastArrScheduleChange = (accountId, channelName = getDefaultQueryCrossTabChannel()) => {
  if (!accountId || !isBrowserRuntime() || typeof window.BroadcastChannel === 'undefined') return;
  let channel;
  try {
    channel = new window.BroadcastChannel(`${channelName}-arr-schedules`);
    channel.postMessage({type: 'arr-schedules-changed', accountId});
  } catch (error) {
    logCrossTabSyncDebug('ARR change could not be sent to other tabs.', error);
  } finally {
    try { channel?.close(); } catch (error) {
      logCrossTabSyncDebug('ARR change channel could not be closed.', error);
    }
  }
};

export const subscribeArrScheduleChanges = (queryClient, channelName) => {
  const channel = new window.BroadcastChannel(`${channelName}-arr-schedules`);
  channel.onmessage = ({data}) => {
    if (data?.type !== 'arr-schedules-changed' || typeof data.accountId !== 'string' || !data.accountId) return;
    void invalidateArrScheduleConsumers(queryClient, data.accountId).catch(error => {
      logCrossTabSyncDebug('ARR results could not be refreshed.', error);
    });
  };
  return () => channel.close();
};

export const initQueryCrossTabSync = (queryClient, options = {}) => {
  if (!queryClient || !isBrowserRuntime()) {
    return () => {};
  }

  if (typeof window.BroadcastChannel === 'undefined') {
    logCrossTabSyncDebug('BroadcastChannel is unavailable; skipping cross-tab query sync.');
    return () => {};
  }

  const broadcastChannel = options?.broadcastChannel || getDefaultQueryCrossTabChannel(options);
  if (!window[SYNC_REGISTRY_KEY] || typeof window[SYNC_REGISTRY_KEY] !== 'object') {
    window[SYNC_REGISTRY_KEY] = {};
  }

  const registry = window[SYNC_REGISTRY_KEY];
  const existingRegistration = registry[broadcastChannel];
  if (
    existingRegistration
    && existingRegistration.queryClient === queryClient
    && typeof existingRegistration.cleanup === 'function'
  ) {
    logCrossTabSyncDebug('Reusing existing broadcast registration.', { broadcastChannel });
    return existingRegistration.cleanup;
  }

  if (existingRegistration && typeof existingRegistration.cleanup === 'function') {
    try {
      existingRegistration.cleanup();
      logCrossTabSyncDebug('Disposed stale broadcast registration before re-registering.', { broadcastChannel });
    } catch (error) {
      console.error('[QueryCrossTabSync] Failed disposing stale broadcast registration.', error);
    }
  }

  const cleanups = [];
  const cleanup = () => cleanups.forEach(stop => stop());
  try {
    const maybeCleanup = broadcastQueryClient({
      queryClient,
      broadcastChannel,
      options: options?.broadcastOptions,
    });
    if (typeof maybeCleanup === 'function') cleanups.push(maybeCleanup);
    cleanups.push(subscribeRenewalMatchChanges(queryClient, broadcastChannel));
    cleanups.push(subscribeArrScheduleChanges(queryClient, broadcastChannel));
    logCrossTabSyncDebug('Initialized broadcast query sync.', { broadcastChannel });
  } catch (error) {
    cleanup();
    console.error('[QueryCrossTabSync] Failed to initialize cross-tab sync.', error);
    return () => {};
  }

  const wrappedCleanup = () => {
    try {
      cleanup();
    } catch (error) {
      console.error('[QueryCrossTabSync] Failed while cleaning up broadcast sync.', error);
    } finally {
      if (registry[broadcastChannel]?.queryClient === queryClient) {
        delete registry[broadcastChannel];
      }
      logCrossTabSyncDebug('Cleaned up broadcast query sync.', { broadcastChannel });
    }
  };

  registry[broadcastChannel] = {
    queryClient,
    cleanup: wrappedCleanup,
  };

  return wrappedCleanup;
};
